// File: frontend/src/pages/profile/MyProfile.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';

const MyProfile = () => {
  const { user, logout } = useAuth();
  const { school } = useSchool();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form states for password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Fetch current user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await api.get('/api/v1/users/profile');
        if (res.data?.success) {
          setProfileData(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle Change Password Submission
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill out all password fields.');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation password do not match.');
      return;
    }

    try {
      setSavingPassword(true);
      const res = await api.put('/api/v1/users/profile/password', {
        currentPassword,
        newPassword,
        confirmPassword
      });

      if (res.data?.success) {
        setPasswordSuccess('Your password has been updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError(res.data?.message || 'Failed to update password.');
      }
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordError(err.response?.data?.message || 'Failed to update password.');
    } finally {
      setSavingPassword(false);
    }
  };

  // Helper function to resolve dashboard path by user role
  const getDashboardPath = (role) => {
    const normRole = role?.toLowerCase();
    switch (normRole) {
      case 'super_admin':
      case 'admin':
        return '/dashboard/admin';
      case 'principal':
        return '/dashboard/principal';
      case 'accountant':
        return '/dashboard/accountant';
      case 'teacher':
        return '/dashboard/teacher';
      case 'librarian':
        return '/dashboard/librarian';
      case 'parent':
        return '/parent/dashboard';
      case 'student':
        return '/dashboard/student';
      default:
        return '/dashboard/admin';
    }
  };

  const currentUserData = profileData || user;
  const roleName = currentUserData?.role ? currentUserData.role.replace('_', ' ').toUpperCase() : 'USER';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Header Navbar */}
      <header className="bg-slate-900 text-white border-b border-slate-800 sticky top-0 z-10 shadow-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(getDashboardPath(user?.role))}
              className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-1.5 text-xs font-bold transition shadow"
              title="Return to Main User Interface"
            >
              <span>🏠</span> Dashboard
            </button>
            <div>
              <h1 className="font-bold text-white text-lg leading-tight">My Profile</h1>
              <p className="text-xs text-slate-400">Account details & password management</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(getDashboardPath(user?.role))}
              className="bg-slate-800 hover:bg-slate-700 text-blue-400 font-semibold px-3 py-2 rounded-xl text-xs transition border border-slate-700 flex items-center gap-1"
            >
              <span>📊</span> Main Interface
            </button>
            <button
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
              className="bg-slate-800 hover:bg-red-900/80 hover:text-white text-slate-300 font-medium px-4 py-2 rounded-xl text-xs transition border border-slate-700"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* User Banner Header */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-800 rounded-2xl p-6 sm:p-8 text-white shadow-xl flex flex-col sm:flex-row sm:items-center justify-between gap-6 border border-slate-800">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-extrabold text-3xl shadow-lg border-2 border-indigo-400 shrink-0">
              {currentUserData?.name ? currentUserData.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <div className="space-y-1">
              <span className="inline-block px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-xs font-extrabold tracking-wider border border-indigo-500/30">
                {roleName}
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold">
                {currentUserData?.name || 'User Account'}
              </h2>
              <p className="text-slate-300 text-sm font-mono">
                {currentUserData?.email || 'N/A'}
              </p>
            </div>
          </div>
          <div className="text-right text-xs text-slate-400 bg-white/5 p-4 rounded-xl border border-white/10 shrink-0">
            <div className="font-semibold text-slate-200">School Institution</div>
            <div className="text-indigo-300 font-bold text-sm mt-0.5">{school?.name || currentUserData?.school_name || 'School System'}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Card 1: User Account Information */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-6">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-primary flex items-center justify-center font-bold text-lg">
                👤
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-base">Account Details</h3>
                <p className="text-xs text-slate-500">Your profile metadata in the system</p>
              </div>
            </div>

            <div className="space-y-4 text-sm">
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase block mb-1">Full Name</label>
                <div className="font-bold text-slate-800 text-base bg-slate-50 p-3 rounded-xl border border-slate-200">
                  {currentUserData?.name || 'N/A'}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase block mb-1">Email Address</label>
                <div className="font-mono font-medium text-slate-800 bg-slate-50 p-3 rounded-xl border border-slate-200">
                  {currentUserData?.email || 'N/A'}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase block mb-1">Assigned Role</label>
                  <div className="font-bold text-indigo-700 bg-indigo-50 p-3 rounded-xl border border-indigo-100 capitalize">
                    {currentUserData?.role ? currentUserData.role.replace('_', ' ') : 'N/A'}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase block mb-1">Account Status</label>
                  <div className="font-bold text-emerald-700 bg-emerald-50 p-3 rounded-xl border border-emerald-100 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Active
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase block mb-1">Last Login Time</label>
                <div className="text-xs text-slate-600 font-mono bg-slate-50 p-3 rounded-xl border border-slate-200">
                  {currentUserData?.last_login ? new Date(currentUserData.last_login).toLocaleString() : 'Active Current Session'}
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Security & Change Password */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-6">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-lg">
                🔐
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-base">Change Password</h3>
                <p className="text-xs text-slate-500">Update your security credentials</p>
              </div>
            </div>

            {passwordError && (
              <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs flex items-center gap-2">
                <span>⚠️ {passwordError}</span>
              </div>
            )}

            {passwordSuccess && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-xs flex items-center gap-2">
                <span>✅ {passwordSuccess}</span>
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Current Password *</label>
                <input
                  type="password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">New Password * (Min 8 chars)</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Confirm New Password *</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                />
              </div>

              <button
                type="submit"
                disabled={savingPassword}
                className="w-full bg-primary hover:bg-blue-600 text-white font-bold py-3 rounded-xl transition duration-200 shadow-md text-sm disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {savingPassword ? 'Updating Password...' : 'Save New Password'}
              </button>
            </form>
          </div>

        </div>

      </main>
    </div>
  );
};

export default MyProfile;
